import { useFlag } from '@calendly/feature-check-react'
import { useMemo } from 'react'

import { useGetOrganizationUuid } from 'common'
import { buildFilters } from 'components/meetings/filter_modal/utils'
import { Periods, allowedScopeTypes } from 'constants/meetings'
import { useScope } from 'hooks/meetings/use_scope'
import {
  useGetMeetingsFilterCountQuery,
  useSearchMeetingsSupergraphFilterCountQuery,
} from 'shared/api/meetings'

import { mapScopeTypeToBackendValue } from '../utils'
import { buildSupergraphCountRequest } from './search_data/supergraph_meeting_search_utils'
import { useSelectedView } from './use_selected_view'

/** Lifetime / unfiltered params — any meeting in the current scope, any time. */
const LIFETIME_FILTER_PARAMS = Object.freeze({
  period: Periods.ALL_TIME,
})

/**
 * True when the current Calendar scope has never had any meetings (all-time count === 0).
 * Uses the existing filter-count GraphQL (no backend activation change). Scope-aware so
 * picking another user from the scope dropdown reflects that user’s history.
 */
export const useNeverHadMeetings = () => {
  const { scopeType, scopeId } = useScope()
  const selectedView = useSelectedView()
  const isSupergraphEnabled = useFlag('sa_meetings_page_with_supergraph', false)
  const organizationUuid = useGetOrganizationUuid()

  const countVariables = useMemo(
    () => ({
      forCount: true,
      ...buildFilters(
        scopeType,
        scopeId,
        selectedView,
        LIFETIME_FILTER_PARAMS,
        true,
        allowedScopeTypes,
        mapScopeTypeToBackendValue,
      ),
    }),
    [scopeType, scopeId, selectedView],
  )

  const supergraphCountRequest = useMemo(
    () => (isSupergraphEnabled && organizationUuid
      ? buildSupergraphCountRequest({ ...countVariables, organizationUuid })
      : null),
    [isSupergraphEnabled, organizationUuid, countVariables],
  )

  const {
    data: legacyData,
    isLoading: legacyLoading,
    isFetching: legacyFetching,
  } = useGetMeetingsFilterCountQuery(countVariables, { skip: isSupergraphEnabled })

  const {
    data: superData,
    isLoading: superLoading,
    isFetching: superFetching,
  } = useSearchMeetingsSupergraphFilterCountQuery(
    supergraphCountRequest ?? { path: 'listMeetings', inputs: [] },
    { skip: !isSupergraphEnabled || !supergraphCountRequest },
  )

  const isLoading = isSupergraphEnabled
    ? (superLoading || superFetching)
    : (legacyLoading || legacyFetching)

  let total = 0
  if (isSupergraphEnabled) {
    total = typeof superData === 'number' ? superData : 0
  } else {
    total = legacyData?.meetings?.total ?? 0
  }

  return {
    neverHadMeetings: !isLoading && total === 0,
    isLoading,
  }
}
